import { useState, useEffect } from 'react';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Input,
  Button,
  Switch,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui';
import { Skeleton } from '@/components/custom';
import { useRequestGetPaymentPackages, useRequestSetPaymentPackages } from '@/hooks/httpRequest';
import { notify } from '@/functions';
import { HeaderPage } from '../components';
import type { ICoinPackage, ICoinPackages, IIncognitoPackage, IIncognitoPackages, IBoostPackage, IBoostPackages } from '@/interfaces/paymentPackage.interface';
import { BoostPackageNameEnum, CoinPackageNameEnum, IncognitoPackageNameEnum } from '@/enums/coinPackageName.enum';

type Continent = 'AFRICA' | 'EUROPE';
type PackageTab = 'coins' | 'incognito' | 'boost';

const CONTINENTS: { value: Continent; label: string }[] = [
  { value: 'AFRICA', label: 'Afrique' },
  { value: 'EUROPE', label: 'Europe' },
];

const CURRENCY_OPTIONS_BY_CONTINENT = {
  AFRICA: ['XAF', 'XOF'],
  EUROPE: ['EUR'],
} as const satisfies Record<Continent, readonly string[]>;

const PACKAGE_LABELS: Record<CoinPackageNameEnum | IncognitoPackageNameEnum | BoostPackageNameEnum, string> = {
  [CoinPackageNameEnum.DIAMOND]: 'Diamond',
  [CoinPackageNameEnum.GOLD]: 'Gold',
  [CoinPackageNameEnum.SILVER]: 'Silver',
  [CoinPackageNameEnum.BRONZE]: 'Bronze',
  [IncognitoPackageNameEnum.INCOGNITO_1_MONTH]: 'Incognito 1 mois',
  [IncognitoPackageNameEnum.INCOGNITO_3_MONTHS]: 'Incognito 3 mois',
  [IncognitoPackageNameEnum.INCOGNITO_6_MONTHS]: 'Incognito 6 mois',
  [IncognitoPackageNameEnum.INCOGNITO_12_MONTHS]: 'Incognito 12 mois',
  [BoostPackageNameEnum.BOOST_1_HOUR]: 'Boost 1 h',
  [BoostPackageNameEnum.BOOST_6_HOURS]: 'Boost 6 h',
  [BoostPackageNameEnum.BOOST_24_HOURS]: 'Boost 24 h',
};

const packageLabel = (name: string): string => PACKAGE_LABELS[name as keyof typeof PACKAGE_LABELS] ?? name;

/* ---------- Skeletons / Empty ---------- */

const TableSkeleton = ({ columnCount, rowCount }: { columnCount: number; rowCount: number }) => (
  <TableBody>
    {Array.from({ length: rowCount }, (_, rowIndex) => (
      <TableRow key={rowIndex} className="h-14">
        {Array.from({ length: columnCount }, (_, colIndex) => (
          <TableCell key={colIndex} className="py-2">
            <Skeleton className={colIndex === 0 ? 'h-4 w-28' : 'h-9 w-24 rounded-md'} />
          </TableCell>
        ))}
      </TableRow>
    ))}
  </TableBody>
);

const EmptyTableRow = ({ columnCount }: { columnCount: number }) => (
  <TableBody>
    <TableRow>
      <TableCell colSpan={columnCount} className="py-8 text-center text-sm text-muted-foreground">
        Aucun forfait disponible
      </TableCell>
    </TableRow>
  </TableBody>
);

/* ---------- Shared sub-components ---------- */

const CurrencySelect = ({
  value,
  options,
  onChange,
}: {
  value: string;
  options: readonly string[];
  onChange: (currency: string) => void;
}) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-24">
      <SelectValue placeholder="Devise" />
    </SelectTrigger>
    <SelectContent>
      {options.map(currency => (
        <SelectItem key={currency} value={currency}>
          {currency}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const rowsAreIdenticalToServer = (updatedRows: unknown[], serverRows: unknown[]): boolean =>
  JSON.stringify(updatedRows) === JSON.stringify(serverRows);

/* fr-FR locale: thousands separator = narrow no-break space, decimal separator = comma */
const FR_LOCALE = 'fr-FR';

const amountToDisplayString = (amount: number): string =>
  isNaN(amount) ? '' : new Intl.NumberFormat(FR_LOCALE, { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(amount);

const formatAmountLive = (raw: string): string => {
  /* Keep only digits and the decimal comma */
  const stripped = raw.replace(/[^\d,]/g, '');
  const [integerPart, ...decimalParts] = stripped.split(',');
  const decimalDigits = decimalParts.join('').slice(0, 2);
  if (integerPart === '') return decimalParts.length > 0 ? `,${decimalDigits}` : '';
  const integerFormatted = new Intl.NumberFormat(FR_LOCALE).format(Number(integerPart));
  /* Preserve trailing comma + up to 2 decimal digits the user is actively typing */
  return decimalParts.length > 0 ? `${integerFormatted},${decimalDigits}` : integerFormatted;
};

const parseDisplayedAmount = (displayed: string): number => {
  /* Strip narrow no-break space (U+202F), no-break space (U+00A0), regular space — all used by fr-FR as thousands separators */
  const withoutThousands = displayed.replace(/[\u202F\u00A0 ]/g, '');
  const withDot = withoutThousands.replace(',', '.');
  return parseFloat(withDot);
};

/* Price cell: live fr-FR formatting as the user types, commits on every keystroke and finalises on blur */
const PriceCell = ({
  amount,
  onCommit,
}: {
  amount: number;
  onCommit: (numericValue: number) => void;
}) => {
  const [displayValue, setDisplayValue] = useState(() => amountToDisplayString(amount));

  /* Sync when parent resets the amount (e.g. cancel) */
  useEffect(() => {
    setDisplayValue(amountToDisplayString(amount));
  }, [amount]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatAmountLive(e.target.value);
    setDisplayValue(formatted);
    /* Commit immediately so the parent state (and hasUnsavedChanges) updates on every keystroke */
    const numericValue = parseDisplayedAmount(formatted);
    if (!isNaN(numericValue)) onCommit(numericValue);
  };

  const handleBlur = () => {
    const numericValue = parseDisplayedAmount(displayValue);
    const committed = isNaN(numericValue) ? 0 : numericValue;
    /* Only commit if the value actually changed — avoids spurious dirty writes when
       the user clicks away (e.g. switches continent) without having edited the field. */
    if (committed !== amount) onCommit(committed);
    setDisplayValue(amountToDisplayString(committed));
  };

  return (
    <Input
      className="w-32 text-right"
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      inputMode="decimal"
    />
  );
};

/* ---------- Validation errors ---------- */

const ValidationErrors = ({ errors }: { errors: string[] }) => {
  if (errors.length === 0) return null;
  return (
    <ul className="text-sm text-destructive flex flex-col gap-1">
      {errors.map(errorMessage => (
        <li key={errorMessage}>{errorMessage}</li>
      ))}
    </ul>
  );
};

/* ---------- Default pack ---------- */

/* Packs no longer offered in the app (RETIRED_PACKS of the API): never the default */
const RETIRED_PACKS: string[] = [IncognitoPackageNameEnum.INCOGNITO_12_MONTHS];

type DefaultablePackage = { name: string; discountPrice: number; selected?: boolean };

/* With no default yet, the cheapest pack on sale is preselected, so the first save is valid */
const withDefaultPackage = <T extends DefaultablePackage>(packages: T[]): T[] => {
  if (packages.some(pkg => pkg.selected)) return packages;
  const cheapest = packages
    .filter(pkg => !RETIRED_PACKS.includes(pkg.name) && pkg.discountPrice > 0)
    .reduce<T | undefined>((min, pkg) => (!min || pkg.discountPrice < min.discountPrice ? pkg : min), undefined);
  return cheapest ? packages.map(pkg => ({ ...pkg, selected: pkg === cheapest })) : packages;
};

const validateDefaultPackage = (packages: DefaultablePackage[]): string[] =>
  packages.length > 0 && packages.filter(pkg => pkg.selected).length !== 1 ? ['Choisissez un forfait par défaut'] : [];

/* the save sends both continents, so the one not on screen is checked too */
const validateBothContinents = <T extends DefaultablePackage>(
  selectedContinent: Continent,
  packagesToSave: Record<Continent, T[]>,
  validateRows: (rows: T[]) => string[],
): string[] =>
  CONTINENTS.flatMap(({ value, label }) => {
    const rows = packagesToSave[value];
    const errors = [...validateRows(rows), ...validateDefaultPackage(rows)];
    return value === selectedContinent ? errors : errors.map(message => `${label} : ${message}`);
  });

/* each continent: its pending edits, else the server rows */
const packagesToSaveOf = <T,>(
  pendingEdits: Partial<Record<Continent, T[]>>,
  serverPackages: Partial<Record<Continent, T[]>>,
  prepare: (rows: T[]) => T[],
): Record<Continent, T[]> => ({
  AFRICA: prepare(pendingEdits.AFRICA ?? serverPackages.AFRICA ?? []),
  EUROPE: prepare(pendingEdits.EUROPE ?? serverPackages.EUROPE ?? []),
});

const DefaultPackageCell = ({
  pkg,
  onSetDefault,
}: {
  pkg: DefaultablePackage;
  onSetDefault: () => void;
}) => {
  const isRetired = RETIRED_PACKS.includes(pkg.name);
  return (
    <TableCell title={isRetired ? "Pack retiré, non proposé dans l'app" : undefined}>
      <Switch
        checked={!!pkg.selected}
        disabled={isRetired}
        onCheckedChange={checked => checked && onSetDefault()}
        aria-label={`${packageLabel(pkg.name)} par défaut`}
      />
    </TableCell>
  );
};

/* ---------- Save / Cancel footer ---------- */

const PackageTableFooter = ({
  hasUnsavedChanges,
  isSaving,
  validationErrors,
  onCancel,
  onSave,
}: {
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  validationErrors: string[];
  onCancel: () => void;
  onSave: () => void;
}) => (
  <div className="flex flex-col gap-2">
    <ValidationErrors errors={validationErrors} />
    <div className="flex justify-end gap-2">
      {hasUnsavedChanges && (
        <Button variant="outline" onClick={onCancel} disabled={isSaving}>
          Annuler
        </Button>
      )}
      <Button onClick={onSave} disabled={!hasUnsavedChanges || isSaving || validationErrors.length > 0}>
        {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
      </Button>
    </div>
  </div>
);

/* ---------- Coin packages ---------- */


const validateCoinPackages = (packages: ICoinPackage[]): string[] => {
  const errors: string[] = [];
  packages.forEach((pkg, rowIndex) => {
    const label = `Ligne ${rowIndex + 1} (${packageLabel(pkg.name)})`;
    if (!pkg.currency) errors.push(`${label} : devise requise`);
    if (pkg.coins <= 0) errors.push(`${label} : le nombre de pièces doit être > 0`);
    if (pkg.fullPrice <= 0) errors.push(`${label} : le prix plein doit être > 0`);
    if (pkg.discountPrice < 0) errors.push(`${label} : le prix réduit ne peut pas être négatif`);
    if (pkg.discountPrice > pkg.fullPrice) errors.push(`${label} : le prix réduit ne peut pas dépasser le prix plein`);
  });
  return errors;
};

const CoinPackagesContent = ({ selectedContinent }: { selectedContinent: Continent }) => {
  /* Pending edits are stored per-continent independently — switching continent never bleeds edits across. */
  const [pendingEditsByContinent, setPendingEditsByContinent] = useState<Partial<ICoinPackages>>({});

  const { packages: serverPackages, isLoading } = useRequestGetPaymentPackages('coin');
  const { triggerSetPackages, isLoadingSetPackages } = useRequestSetPaymentPackages('coin', () => {
    setPendingEditsByContinent(prev => { const next = { ...prev }; delete next[selectedContinent]; return next; });
  });

  const serverPackagesTyped = serverPackages as ICoinPackages;
  const serverRows = serverPackagesTyped[selectedContinent] ?? [];
  const baseRows = pendingEditsByContinent[selectedContinent] ?? serverRows;
  const packagesToSave = packagesToSaveOf(pendingEditsByContinent, serverPackagesTyped, withDefaultPackage);
  const displayedPackages = packagesToSave[selectedContinent];
  const hasUnsavedChanges = selectedContinent in pendingEditsByContinent || !rowsAreIdenticalToServer(displayedPackages, serverRows);

  const liveValidationErrors = hasUnsavedChanges
    ? validateBothContinents(selectedContinent, packagesToSave, validateCoinPackages)
    : [];

  const updatePackageField = (rowIndex: number, field: keyof ICoinPackage, value: number | string) =>
    setRows(baseRows.map((pkg, index) => (index === rowIndex ? { ...pkg, [field]: value } : pkg)));

  // the pack the coin shop opens on: one per continent
  const setDefaultPackage = (rowIndex: number) =>
    setRows(baseRows.map((pkg, index) => ({ ...pkg, selected: index === rowIndex })));

  const setRows = (updatedRows: ICoinPackage[]) => {
    setPendingEditsByContinent(prev => {
      if (rowsAreIdenticalToServer(updatedRows, serverRows)) {
        const next = { ...prev };
        delete next[selectedContinent];
        return next;
      }
      return { ...prev, [selectedContinent]: updatedRows };
    });
  };

  const handleCancel = () =>
    setPendingEditsByContinent(prev => { const next = { ...prev }; delete next[selectedContinent]; return next; });

  const handleSave = async () => {
    if (liveValidationErrors.length > 0) return;
    try {
      await triggerSetPackages(packagesToSave);
      notify('Forfaits pièces mis à jour', 'success');
    } catch {
      /* error already notified by the hook */
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nom</TableHead>
            <TableHead>Pièces</TableHead>
            <TableHead>Prix plein</TableHead>
            <TableHead>Prix réduit</TableHead>
            <TableHead>Devise</TableHead>
            <TableHead>Par défaut</TableHead>
          </TableRow>
        </TableHeader>
        {isLoading ? (
          <TableSkeleton columnCount={6} rowCount={4} />
        ) : displayedPackages.length === 0 ? (
          <EmptyTableRow columnCount={6} />
        ) : (
          <TableBody>
            {displayedPackages.map((coinPackage, rowIndex) => (
              <TableRow key={`${selectedContinent}-${coinPackage.name}`}>
                <TableCell className="font-medium">{packageLabel(coinPackage.name)}</TableCell>
                <TableCell>
                  <Input
                    type="number"
                    value={coinPackage.coins}
                    onChange={e => updatePackageField(rowIndex, 'coins', Number(e.target.value))}
                    className="w-24 text-right"
                  />
                </TableCell>
                <TableCell>
                  <PriceCell
                    amount={coinPackage.fullPrice}
                    onCommit={numericValue => updatePackageField(rowIndex, 'fullPrice', numericValue)}
                  />
                </TableCell>
                <TableCell>
                  <PriceCell
                    amount={coinPackage.discountPrice}
                    onCommit={numericValue => updatePackageField(rowIndex, 'discountPrice', numericValue)}
                  />
                </TableCell>
                <TableCell>
                  <CurrencySelect
                    value={coinPackage.currency}
                    options={CURRENCY_OPTIONS_BY_CONTINENT[selectedContinent]}
                    onChange={currency => updatePackageField(rowIndex, 'currency', currency)}
                  />
                </TableCell>
                <DefaultPackageCell pkg={coinPackage} onSetDefault={() => setDefaultPackage(rowIndex)} />
              </TableRow>
            ))}
          </TableBody>
        )}
      </Table>
      <PackageTableFooter
        hasUnsavedChanges={hasUnsavedChanges}
        isSaving={isLoadingSetPackages}
        validationErrors={liveValidationErrors}
        onCancel={handleCancel}
        onSave={handleSave}
      />
    </div>
  );
};

/* ---------- Incognito packages ---------- */


const validateIncognitoPackages = (packages: IIncognitoPackage[]): string[] => {
  const errors: string[] = [];
  packages.forEach((pkg, rowIndex) => {
    const label = `Ligne ${rowIndex + 1} (${packageLabel(pkg.name)})`;
    if (!pkg.currency) errors.push(`${label} : devise requise`);
    if (pkg.nbrMonths <= 0) errors.push(`${label} : le nombre de mois doit être > 0`);
    if (pkg.fullPrice <= 0) errors.push(`${label} : le prix plein doit être > 0`);
    if (pkg.discountPrice < 0) errors.push(`${label} : le prix réduit ne peut pas être négatif`);
    if (pkg.discountPrice > pkg.fullPrice) errors.push(`${label} : le prix réduit ne peut pas dépasser le prix plein`);
    if (pkg.discountPercentage < 0 || pkg.discountPercentage > 100) errors.push(`${label} : la remise doit être entre 0 et 100`);
  });
  return errors;
};

/* The saving shown in the app ("Save x%"): against buying the 1-month pack that many times, to the nearest 5 */
const withComputedDiscounts = (packages: IIncognitoPackage[]): IIncognitoPackage[] => {
  const shortest = [...packages].filter(pkg => pkg.nbrMonths > 0).sort((a, b) => a.nbrMonths - b.nbrMonths)[0];
  const monthPrice = shortest ? shortest.discountPrice / shortest.nbrMonths : 0;
  return packages.map(pkg => {
    const fullPeriodPrice = monthPrice * pkg.nbrMonths;
    const saving = fullPeriodPrice > 0 ? (1 - pkg.discountPrice / fullPeriodPrice) * 100 : 0;
    return { ...pkg, discountPercentage: Math.min(100, Math.max(0, Math.round(saving / 5) * 5)) };
  });
};

const IncognitoPackagesContent = ({ selectedContinent }: { selectedContinent: Continent }) => {
  const [pendingEditsByContinent, setPendingEditsByContinent] = useState<Partial<IIncognitoPackages>>({});

  const { packages: serverPackages, isLoading } = useRequestGetPaymentPackages('incognito');
  const { triggerSetPackages, isLoadingSetPackages } = useRequestSetPaymentPackages('incognito', () => {
    setPendingEditsByContinent(prev => { const next = { ...prev }; delete next[selectedContinent]; return next; });
  });

  const serverPackagesTyped = serverPackages as IIncognitoPackages;
  const serverRows = serverPackagesTyped[selectedContinent] ?? [];
  // the discount is never typed: computed from the prices, saved with them
  const packagesToSave = packagesToSaveOf(pendingEditsByContinent, serverPackagesTyped, rows =>
    withComputedDiscounts(withDefaultPackage(rows)),
  );
  const displayedPackages = packagesToSave[selectedContinent];
  const hasUnsavedChanges = selectedContinent in pendingEditsByContinent || !rowsAreIdenticalToServer(displayedPackages, serverRows);

  const liveValidationErrors = hasUnsavedChanges
    ? validateBothContinents(selectedContinent, packagesToSave, validateIncognitoPackages)
    : [];

  const baseRows = pendingEditsByContinent[selectedContinent] ?? serverRows;
  // the packs the app knows and this continent does not sell yet
  const missingPackNames = Object.values(IncognitoPackageNameEnum).filter(name => !baseRows.some(pkg => pkg.name === name));

  const updatePackageField = (rowIndex: number, field: keyof IIncognitoPackage, value: number | string) =>
    setRows(baseRows.map((pkg, index) => (index === rowIndex ? { ...pkg, [field]: value } : pkg)));

  const setDefaultPackage = (rowIndex: number) =>
    setRows(baseRows.map((pkg, index) => ({ ...pkg, selected: index === rowIndex })));

  // a new pack, prices to fill in before saving; the rows stay in the order of their months, the order of the app
  const addPackage = (name: string) => {
    const nbrMonths = parseInt(name.match(/\d+/)?.[0] ?? '0', 10);
    const currency = baseRows[0]?.currency ?? CURRENCY_OPTIONS_BY_CONTINENT[selectedContinent][0];
    const newRow = { name, nbrMonths, fullPrice: 0, discountPrice: 0, discountPercentage: 0, currency } as IIncognitoPackage;
    setRows([...baseRows, newRow].sort((a, b) => a.nbrMonths - b.nbrMonths));
  };

  const setRows = (updatedRows: IIncognitoPackage[]) => {
    setPendingEditsByContinent(prev => {
      if (rowsAreIdenticalToServer(updatedRows, serverRows)) {
        const next = { ...prev };
        delete next[selectedContinent];
        return next;
      }
      return { ...prev, [selectedContinent]: updatedRows };
    });
  };

  const handleCancel = () =>
    setPendingEditsByContinent(prev => { const next = { ...prev }; delete next[selectedContinent]; return next; });

  const handleSave = async () => {
    if (liveValidationErrors.length > 0) return;
    try {
      await triggerSetPackages(packagesToSave);
      notify('Forfaits incognito mis à jour', 'success');
    } catch {
      /* error already notified by the hook */
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nom</TableHead>
            <TableHead>Mois</TableHead>
            <TableHead>Prix plein</TableHead>
            <TableHead>Prix réduit</TableHead>
            <TableHead>Remise (%)</TableHead>
            <TableHead>Devise</TableHead>
            <TableHead>Par défaut</TableHead>
          </TableRow>
        </TableHeader>
        {isLoading ? (
          <TableSkeleton columnCount={7} rowCount={3} />
        ) : displayedPackages.length === 0 ? (
          <EmptyTableRow columnCount={7} />
        ) : (
          <TableBody>
            {displayedPackages.map((incognitoPackage, rowIndex) => (
              <TableRow key={`${selectedContinent}-${incognitoPackage.name}`}>
                <TableCell className="font-medium">{packageLabel(incognitoPackage.name)}</TableCell>
                <TableCell>
                  <Input
                    type="number"
                    value={incognitoPackage.nbrMonths}
                    onChange={e => updatePackageField(rowIndex, 'nbrMonths', Number(e.target.value))}
                    className="w-20 text-right"
                  />
                </TableCell>
                <TableCell>
                  <PriceCell
                    amount={incognitoPackage.fullPrice}
                    onCommit={numericValue => updatePackageField(rowIndex, 'fullPrice', numericValue)}
                  />
                </TableCell>
                <TableCell>
                  <PriceCell
                    amount={incognitoPackage.discountPrice}
                    onCommit={numericValue => updatePackageField(rowIndex, 'discountPrice', numericValue)}
                  />
                </TableCell>
                <TableCell>
                  <Input
                    type="number"
                    value={incognitoPackage.discountPercentage}
                    readOnly
                    disabled
                    title="Calculée : économie par rapport au forfait 1 mois"
                    className="w-20 text-right"
                  />
                </TableCell>
                <TableCell>
                  <CurrencySelect
                    value={incognitoPackage.currency}
                    options={CURRENCY_OPTIONS_BY_CONTINENT[selectedContinent]}
                    onChange={currency => updatePackageField(rowIndex, 'currency', currency)}
                  />
                </TableCell>
                <DefaultPackageCell pkg={incognitoPackage} onSetDefault={() => setDefaultPackage(rowIndex)} />
              </TableRow>
            ))}
          </TableBody>
        )}
      </Table>
      {!isLoading && missingPackNames.length > 0 && (
        <Select value="" onValueChange={addPackage}>
          <SelectTrigger className="w-56">
            <SelectValue placeholder="Ajouter un forfait" />
          </SelectTrigger>
          <SelectContent>
            {missingPackNames.map(name => (
              <SelectItem key={name} value={name}>
                {packageLabel(name)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <PackageTableFooter
        hasUnsavedChanges={hasUnsavedChanges}
        isSaving={isLoadingSetPackages}
        validationErrors={liveValidationErrors}
        onCancel={handleCancel}
        onSave={handleSave}
      />
    </div>
  );
};

/* ---------- Boost packages ---------- */


const validateBoostPackages = (packages: IBoostPackage[]): string[] => {
  const errors: string[] = [];
  packages.forEach((pkg, rowIndex) => {
    const label = `Ligne ${rowIndex + 1} (${packageLabel(pkg.name)})`;
    if (!pkg.currency) errors.push(`${label} : devise requise`);
    if (pkg.durationHours <= 0) errors.push(`${label} : la durée doit être > 0`);
    if (pkg.fullPrice <= 0) errors.push(`${label} : le prix plein doit être > 0`);
    if (pkg.discountPrice < 0) errors.push(`${label} : le prix réduit ne peut pas être négatif`);
    if (pkg.discountPrice > pkg.fullPrice) errors.push(`${label} : le prix réduit ne peut pas dépasser le prix plein`);
  });
  return errors;
};

const BoostPackagesContent = ({ selectedContinent }: { selectedContinent: Continent }) => {
  const [pendingEditsByContinent, setPendingEditsByContinent] = useState<Partial<IBoostPackages>>({});

  const { packages: serverPackages, isLoading } = useRequestGetPaymentPackages('boost');
  const { triggerSetPackages, isLoadingSetPackages } = useRequestSetPaymentPackages('boost', () => {
    setPendingEditsByContinent(prev => { const next = { ...prev }; delete next[selectedContinent]; return next; });
  });

  const serverPackagesTyped = serverPackages as IBoostPackages;
  const serverRows = serverPackagesTyped[selectedContinent] ?? [];
  const baseRows = pendingEditsByContinent[selectedContinent] ?? serverRows;
  const packagesToSave = packagesToSaveOf(pendingEditsByContinent, serverPackagesTyped, withDefaultPackage);
  const displayedPackages = packagesToSave[selectedContinent];
  const hasUnsavedChanges = selectedContinent in pendingEditsByContinent || !rowsAreIdenticalToServer(displayedPackages, serverRows);

  const liveValidationErrors = hasUnsavedChanges
    ? validateBothContinents(selectedContinent, packagesToSave, validateBoostPackages)
    : [];

  const updatePackageField = (rowIndex: number, field: keyof IBoostPackage, value: number | string) =>
    setRows(baseRows.map((pkg, index) => (index === rowIndex ? { ...pkg, [field]: value } : pkg)));

  const setDefaultPackage = (rowIndex: number) =>
    setRows(baseRows.map((pkg, index) => ({ ...pkg, selected: index === rowIndex })));

  const setRows = (updatedRows: IBoostPackage[]) => {
    setPendingEditsByContinent(prev => {
      if (rowsAreIdenticalToServer(updatedRows, serverRows)) {
        const next = { ...prev };
        delete next[selectedContinent];
        return next;
      }
      return { ...prev, [selectedContinent]: updatedRows };
    });
  };

  const handleCancel = () =>
    setPendingEditsByContinent(prev => { const next = { ...prev }; delete next[selectedContinent]; return next; });

  const handleSave = async () => {
    if (liveValidationErrors.length > 0) return;
    try {
      await triggerSetPackages(packagesToSave);
      notify('Forfaits boost mis à jour', 'success');
    } catch {
      /* error already notified by the hook */
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nom</TableHead>
            <TableHead>Durée (h)</TableHead>
            <TableHead>Prix plein</TableHead>
            <TableHead>Prix réduit</TableHead>
            <TableHead>Devise</TableHead>
            <TableHead>Par défaut</TableHead>
          </TableRow>
        </TableHeader>
        {isLoading ? (
          <TableSkeleton columnCount={6} rowCount={3} />
        ) : displayedPackages.length === 0 ? (
          <EmptyTableRow columnCount={6} />
        ) : (
          <TableBody>
            {displayedPackages.map((boostPackage, rowIndex) => (
              <TableRow key={`${selectedContinent}-${boostPackage.name}`}>
                <TableCell className="font-medium">{packageLabel(boostPackage.name)}</TableCell>
                <TableCell>
                  <Input
                    type="number"
                    value={boostPackage.durationHours}
                    onChange={e => updatePackageField(rowIndex, 'durationHours', Number(e.target.value))}
                    className="w-20 text-right"
                  />
                </TableCell>
                <TableCell>
                  <PriceCell
                    amount={boostPackage.fullPrice}
                    onCommit={numericValue => updatePackageField(rowIndex, 'fullPrice', numericValue)}
                  />
                </TableCell>
                <TableCell>
                  <PriceCell
                    amount={boostPackage.discountPrice}
                    onCommit={numericValue => updatePackageField(rowIndex, 'discountPrice', numericValue)}
                  />
                </TableCell>
                <TableCell>
                  <CurrencySelect
                    value={boostPackage.currency}
                    options={CURRENCY_OPTIONS_BY_CONTINENT[selectedContinent]}
                    onChange={currency => updatePackageField(rowIndex, 'currency', currency)}
                  />
                </TableCell>
                <DefaultPackageCell pkg={boostPackage} onSetDefault={() => setDefaultPackage(rowIndex)} />
              </TableRow>
            ))}
          </TableBody>
        )}
      </Table>
      <PackageTableFooter
        hasUnsavedChanges={hasUnsavedChanges}
        isSaving={isLoadingSetPackages}
        validationErrors={liveValidationErrors}
        onCancel={handleCancel}
        onSave={handleSave}
      />
    </div>
  );
};

/* ---------- Continent toggle — separate Radix Tabs instance, fully isolated context ---------- */

const ContinentToggle = ({
  selectedContinent,
  onContinentChange,
}: {
  selectedContinent: Continent;
  onContinentChange: (continent: Continent) => void;
}) => (
  <Tabs value={selectedContinent} onValueChange={value => onContinentChange(value as Continent)}>
    <TabsList>
      {CONTINENTS.map(({ value, label }) => (
        <TabsTrigger key={value} value={value}>
          {label}
        </TabsTrigger>
      ))}
    </TabsList>
  </Tabs>
);

/* ---------- Page ---------- */

const PaymentPackagesPage = () => {
  const [selectedContinent, setSelectedContinent] = useState<Continent>('AFRICA');
  const [selectedPackageTab, setSelectedPackageTab] = useState<PackageTab>('coins');

  return (
    <div className="flex flex-col gap-6">
      <HeaderPage
        title="Forfaits"
        description="Gérez les prix des forfaits de pièces, incognito et boost par continent."
      />
      <Tabs value={selectedPackageTab} onValueChange={value => setSelectedPackageTab(value as PackageTab)}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="coins">Pièces</TabsTrigger>
            <TabsTrigger value="incognito">Incognito</TabsTrigger>
            <TabsTrigger value="boost">Boost</TabsTrigger>
          </TabsList>
          <ContinentToggle
            selectedContinent={selectedContinent}
            onContinentChange={setSelectedContinent}
          />
        </div>
        <div className="mt-4">
          <TabsContent value="coins">
            <CoinPackagesContent selectedContinent={selectedContinent} />
          </TabsContent>
          <TabsContent value="incognito">
            <IncognitoPackagesContent selectedContinent={selectedContinent} />
          </TabsContent>
          <TabsContent value="boost">
            <BoostPackagesContent selectedContinent={selectedContinent} />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

export default PaymentPackagesPage;
